/**
 * fifaToDdsShape — pure FIFA → DDS-PLS context mapper.
 *
 * Caption definitions bind against the DDS data shape
 * (`fixture[0].fixture_id`, `$fixture.home_team.team_name`, ...). A
 * FIFA-sourced caption therefore has to present its data in that same
 * shape, otherwise the bindings resolve to empty. This function does
 * that translation; pair it with `withContextShape` to decorate the
 * raw AetherFifaContext.
 *
 * Input: the FIFA fixture RECORD from allUpcomingFixtures.
 * Output: a DDS-shaped context — `{ fixture: [ { ... } ] }`.
 *
 * ── WHAT IS MAPPED (the identity layer) ─────────────────────────────
 *   fixture_id, fixture_name, fixture_status, fixture_date,
 *   kick_off_time, competition_id, season_id, home_team_id,
 *   away_team_id, attendance, venue[], and home_team[]/away_team[]
 *   with team_id / team_name / team_tricode.
 *
 * ── WHAT IS NOT MAPPED — deferred / not mappable ────────────────────
 * Kept as a standing note so nothing is silently lost:
 *
 *   1. STATS / METRICS — not mappable by reshape.
 *      FIFA stats are a name-keyed bag
 *      (fixtureTeamMetrics[].metrics[{metricName, metricValue}],
 *      athleteMetrics[...]), sourced from the separate
 *      `fixtureMetricChannel` query. DDS stats are typed arrays
 *      (home_team[0].stats[], fixture.event[]). Different structure
 *      AND different stat names — a stats caption is source-specific
 *      by nature. When FIFA live stats are needed, AetherFifaContext
 *      gains a second fetch (fixtureMetricChannel) and the raw block
 *      is exposed for FIFA-specific bindings; it is NOT folded into
 *      the DDS shape.
 *
 *   2. FIELDS FIFA's API SIMPLY DOESN'T HAVE — never coverable:
 *      kit colours (*_colour_*), league standings / live_standing,
 *      match officials, lineups. Captions binding these are blank in
 *      FIFA mode regardless.
 *      NOTE: team_short_name IS populated — FIFA gives one team name,
 *      so it fills both team_name and team_short_name. And
 *      fixture_team_setting.kit_type is defaulted to "<tricode>_H"
 *      (FIFA has no kit data, but the Home kit is a safe default).
 *
 *   3. SCORES — FIFA has homeTeamScore / awayTeamScore on the record,
 *      but DDS carries scores in fixture_result / event / stats (not
 *      a flat fixture[0] field). Left in `_fifaRaw` rather than
 *      invented onto a DDS path. Revisit if a score-bound caption
 *      needs a defined home/away score path.
 */

/** Split "Home v Away" / "Home vs Away" → [home, away] | [null, null]. */
function splitFixtureName(name) {
  if (typeof name !== "string") return [null, null];
  const parts = name.split(/\s+vs?\s+/i);
  if (parts.length === 2) return [parts[0].trim(), parts[1].trim()];
  return [null, null];
}

/**
 * ISO "2026-06-13T19:00:00.000Z" → DDS-style { date, time }:
 * date "2026-06-13Z", time "19:00:00Z" (millis + Z stripped, then Z
 * re-appended — matches the DDS fixture_date / kick_off_time format).
 */
function splitDateTime(iso) {
  if (typeof iso !== "string" || !iso.includes("T")) return { date: "", time: "" };
  const [datePart, timePartRaw] = iso.split("T");
  const time = timePartRaw.split(".")[0].replace(/Z$/, "");
  return { date: `${datePart}Z`, time: `${time}Z` };
}

/** Resolve venue → { venue_name } whether FIFA gives a string or object. */
function venueName(venue) {
  if (typeof venue === "string") return venue;
  if (venue && typeof venue === "object") return venue.venue_name || venue.name || "";
  return "";
}

/**
 * @param {object} record  FIFA fixture record (allUpcomingFixtures)
 * @param {{ fixtureId?: string }} meta
 * @returns {object} DDS-shaped context (or {} when no fixtureId)
 */
export function fifaToDdsShape(record, meta) {
  const fixtureId = meta && meta.fixtureId;
  if (!fixtureId) return {};

  const fx = record || {};

  // Team names — prefer the explicit record fields; fall back to
  // splitting fixtureName ("Home v Away") when they're absent.
  let homeName = fx.homeTeamName;
  let awayName = fx.awayTeamName;
  if (!homeName || !awayName) {
    const [splitHome, splitAway] = splitFixtureName(fx.fixtureName);
    homeName = homeName || splitHome;
    awayName = awayName || splitAway;
  }

  const { date, time } = splitDateTime(fx.startDateTime);

  return {
    fixture: [
      {
        fixture_id: fixtureId,
        fixture_name: fx.fixtureName || "",
        fixture_status: fx.fixtureStatus || "",
        fixture_date: date,
        kick_off_time: time,
        competition_id: fx.aetCompetitionId || "",
        season_id: fx.aetSeasonId || "",
        home_team_id: fx.aetHomeTeamId || "",
        away_team_id: fx.aetAwayTeamId || "",
        attendance: fx.attendance ?? "",
        // FIFA exposes a single team name (homeTeamName). DDS splits
        // it into team_name ("Liverpool FC") + team_short_name
        // ("Liverpool"); FIFA doesn't distinguish, so the one name
        // populates BOTH slots — captions binding either resolve.
        //
        // fixture_team_setting.kit_type: FIFA has no kit data, so we
        // default it to "<tricode>_H" (the team's Home kit). Captions
        // binding kit_type directly get a sensible value instead of
        // blank; those with their own "tricode & '_H'" fallback see
        // the same result either way.
        home_team: [
          {
            team_id: fx.aetHomeTeamId || "",
            team_name: homeName || "",
            team_short_name: homeName || "",
            team_tricode: fx.extHomeTeamCode || "",
            fixture_team_setting: [
              { kit_type: fx.extHomeTeamCode ? `${fx.extHomeTeamCode}_H` : "" },
            ],
          },
        ],
        away_team: [
          {
            team_id: fx.aetAwayTeamId || "",
            team_name: awayName || "",
            team_short_name: awayName || "",
            team_tricode: fx.extAwayTeamCode || "",
            fixture_team_setting: [
              { kit_type: fx.extAwayTeamCode ? `${fx.extAwayTeamCode}_H` : "" },
            ],
          },
        ],
        venue: [
          {
            venue_name: venueName(fx.venue),
            venue_city: fx.city || "",
          },
        ],
        // Raw FIFA record kept intact for fields not in the DDS shape
        // (scores, minute/second, ext ids) — FIFA-specific bindings.
        _fifaRaw: fx,
      },
    ],
  };
}
